import { useCallback, useState } from 'react';

import { getClassFlags } from './helpers';

import type { CustomField } from 'bundles/apex/custom_fields/types';
import type { FormFeatures, FormOptions, FormState } from './types';

export function useCustomFieldForm(
  initialField: CustomField,
  initialOptions: FormOptions,
  features: FormFeatures = {},
) {
  const [formField, setFormField] = useState(initialize(initialField));
  const [formOptions, setFormOptions] = useState(getOptions(formField, initialOptions));

  const resetFormField = useCallback((initialField: CustomField) => {
    const field = initialize(initialField);
    setFormField(field);
    setFormOptions(getOptions(field, initialOptions));
  }, []);

  const _setFormField = useCallback((field: FormState) => {
    field.classNames = getClassNames(field);

    if (features.edge) {
      field.marketing = getMarketingConfig(field);
    }

    const options = getOptions(field, initialOptions);
    updateFieldForAvailableOptions(field, options);

    setFormField(field);
    setFormOptions(options);
  }, []);

  return {
    formField,
    options: formOptions,
    setFormField: _setFormField,
    resetFormField,
  };
}

function initialize(field: CustomField): FormState {
  return {
    ...field,
    classFlags: getClassFlags(field),
  };
}

function getClassNames(field: FormState) {
  const classNames: string[] = [];

  if (field.classFlags.deal) {
    classNames.push('Apex::Deal');
  }

  if (field.classFlags.property) {
    classNames.push('Research::Property');
  }

  if (field.classFlags.comp) {
    let valid = false;

    if (hasSaleDealType(field)) {
      classNames.push('Research::Sale');
      valid = true;
    }

    if (hasLeaseDealType(field)) {
      classNames.push('Research::Lease');
      valid = true;
    }

    if (!valid) {
      // Invalid class name, but hooked up to clean validation
      classNames.push('Research::Transaction');
    }
  }

  if (field.classFlags.contact) {
    classNames.push('Backoffice::Contact');
  }

  return classNames;
}

function getMarketingConfig(field: FormState) {
  let marketing = field.marketing;

  if (field.classFlags.deal && field.classFlags.comp) {
    const isSaleComp = hasSaleDealType(field);
    const isLeaseComp = hasLeaseDealType(field);
    if (isSaleComp && !isLeaseComp) {
      marketing = { type: 'Sale Comp' };
    } else if (isLeaseComp && !isSaleComp) {
      marketing = { type: 'Lease Comp' };
    } else {
      marketing = { type: 'Sale and Lease Comp' };
    }

    marketing.useNameForField = true;
  } else if (!field.classFlags.deal) {
    marketing = null;
  }

  return marketing;
}

function getOptions(field: FormState, allOptions: FormOptions) {
  const options = { ...allOptions };

  if (!field.classFlags.deal) {
    options.categories = [];
    options.stages = [];
  }

  if (field.classFlags.comp && !field.classFlags.deal) {
    options.dealTypes = options.dealTypes.filter(option => ['sale', 'lease'].includes(option.value));
  }

  if (field.classFlags.comp && !field.classFlags.contact && !field.classFlags.deal && !field.classFlags.property) {
    options.types = allOptions.types.filter(option => option.forCustomFieldSchemas);
  }

  return options;
}

function updateFieldForAvailableOptions(field: FormState, options: FormOptions) {
  if (!options.categories.length) {
    delete field.category;
  }

  const availableDealTypes = options.dealTypes.map(option => option.value);
  field.dealTypes = field.dealTypes.filter(dealType => availableDealTypes.includes(dealType));

  const hasStages = options.stages && options.stages.length > 0;
  const isStageEmpty = !field.addToStage;

  if (hasStages && isStageEmpty) {
    field.addToStage = options.stages[0].value;
  }

  if (field.type && !options.types.map(option => option.value).includes(field.type)) {
    field.type = null;
  }
}

function hasSaleDealType(field: FormState) {
  return field.dealTypes.includes('sale') || field.dealTypes.includes('buyer_rep');
}

function hasLeaseDealType(field: FormState) {
  return field.dealTypes.includes('lease') || field.dealTypes.includes('tenant_rep');
}
